/**
 * Pure builder for the Remake-Apply channel pills (master pill first, then a
 * pill per sales channel with override-count + completeness badges).
 */

import { computeChannelCompleteness } from '@/hooks/useChannelCompleteness'
import { computeInheritanceMap, countOverrides } from '@/hooks/useInheritance'
import {
  MASTER_CHANNEL_ID,
  getChannelName,
} from '@/components/products/v2/tabs/content/channels/channel-constants'
import type { ChannelPillItem } from '@/components/products/v2/shared/ChannelPillsSelector'
import type { JTLDescription } from '@/lib/api/descriptions'

export interface BuildRemakeChannelPillsArgs {
  channelIds: string[]
  channelNames: Record<string, string>
  selectedLang: string
  masterDesc: JTLDescription | undefined
  getChannel: (channelId: string, lang: string) => JTLDescription | undefined
  /** Badge label for channels that have no description yet (e.g. "Neu"). */
  emptyLabel?: string
}

export function resolveChannelName(
  id: string,
  channelNames: Record<string, string>,
): string {
  return channelNames[id.replace('--', '-')] ?? channelNames[id] ?? getChannelName(id)
}

export function buildRemakeChannelPills({
  channelIds,
  channelNames,
  selectedLang,
  masterDesc,
  getChannel,
  emptyLabel,
}: BuildRemakeChannelPillsArgs): ChannelPillItem[] {
  return [
    {
      id: MASTER_CHANNEL_ID,
      name: channelNames[MASTER_CHANNEL_ID] ?? getChannelName(MASTER_CHANNEL_ID),
      isMaster: true,
    },
    ...channelIds
      .filter((id) => id !== MASTER_CHANNEL_ID)
      .map((id) => {
        const desc = getChannel(id, selectedLang)
        if (!desc) {
          // Channel has no description yet → offer it as a new, empty text slot.
          return { id, name: resolveChannelName(id, channelNames), badgeLabel: emptyLabel }
        }
        const overrides = countOverrides(computeInheritanceMap(masterDesc, desc))
        return {
          id,
          name: resolveChannelName(id, channelNames),
          badgeCount: overrides > 0 ? overrides : undefined,
          badgeLabel: computeChannelCompleteness(desc) === 100 ? '✓' : undefined,
        }
      }),
  ]
}
